// src/store/relicVault/actions.js

import {
  SET_RELIC_VAULT_STATE,
  SET_RELIC_VAULT_HYDRATED,
  ADD_OWNED_RELIC,
  REMOVE_OWNED_RELIC,
  ADD_OWNED_COMPANION,
  SET_EQUIPPED_COMPANION,
  SET_STORE_SLOTS,
  ADJUST_RELIC_VAULT_BALANCE,
  RESET_RELIC_VAULT_BALANCE_ADJUSTMENT,
  RESET_RELIC_VAULT,
} from './actionTypes';
import * as relicVaultService from 'utils/relicVault/relicVaultService';
import { getRelic } from 'utils/relicVault/relicDisplay';
import { drawIds, initialStoreSlots } from 'utils/relicVault/storeDeck';
import {
  COMPANION_NONE_ID,
  getCompanionForGuild,
} from 'config/relicVault/companions';
import { getBadge } from 'config/relicVault/badges';
import {
  STORE_SLOT_COUNT,
  REFRESH_COSTS,
  sellRefundFor,
  todayStr,
} from 'config/relicVault/economy';

export const setRelicVaultState = payload => ({
  type: SET_RELIC_VAULT_STATE,
  payload,
});

export const setRelicVaultHydrated = value => ({
  type: SET_RELIC_VAULT_HYDRATED,
  payload: value,
});

export const addOwnedRelic = relicId => ({ type: ADD_OWNED_RELIC, payload: relicId });

export const removeOwnedRelic = relicId => ({
  type: REMOVE_OWNED_RELIC,
  payload: relicId,
});

export const addOwnedCompanion = guildId => ({
  type: ADD_OWNED_COMPANION,
  payload: guildId,
});

export const setEquippedCompanion = guildId => ({
  type: SET_EQUIPPED_COMPANION,
  payload: guildId,
});

export const setStoreSlots = payload => ({ type: SET_STORE_SLOTS, payload });

export const adjustRelicVaultBalance = delta => ({
  type: ADJUST_RELIC_VAULT_BALANCE,
  payload: delta,
});

export const resetRelicVaultBalanceAdjustment = () => ({
  type: RESET_RELIC_VAULT_BALANCE_ADJUSTMENT,
});

/** Reset on logout so the next user doesn't inherit this collection. */
export const resetRelicVault = () => ({ type: RESET_RELIC_VAULT });

/**
 * Daily refresh allowance, accounting for the day having rolled over since the
 * last refresh.
 */
export function refreshAllowanceFor(vault) {
  const usedToday =
    vault.lastRefreshDate === todayStr() ? vault.refreshesUsedToday : 0;
  const left = Math.max(0, REFRESH_COSTS.length - usedToday);
  return {
    usedToday,
    left,
    max: REFRESH_COSTS.length,
    nextCost: left > 0 ? REFRESH_COSTS[usedToday] : 0,
  };
}

/**
 * Apply a successful spend/refund. A real endpoint returns the authoritative
 * `newBalance`: drop the local adjustment and let the caller re-fetch. A stub
 * doesn't, so keep the change as a local adjustment on top of the real balance.
 */
const applyCoins = (dispatch, resp, delta) => {
  if (typeof resp?.newBalance === 'number') {
    dispatch(resetRelicVaultBalanceAdjustment());
    return true;
  }
  if (delta) dispatch(adjustRelicVaultBalance(delta));
  return false;
};

const failure = (error, fallback) => ({
  success: false,
  error: error || fallback,
});

/**
 * Load owned relics/companions for this user and deal the first store slots.
 *
 * The service is a stub today and returns null, which leaves an empty
 * collection. Once it is backed by the database this starts returning real
 * state and nothing here needs to change.
 */
export const hydrateRelicVault = userId => async dispatch => {
  let stored = null;
  try {
    stored = await relicVaultService.fetchVaultState(userId);
  } catch (error) {
    // Still hydrate: an empty collection is a valid state.
    console.warn('[RelicVault] hydrate failed', error?.message || error);
  }

  // Defensively drop ids this build doesn't know, so a catalog change can't
  // wedge the store or the vault.
  const ownedRelicIds = (stored?.ownedRelicIds || []).filter(id => getRelic(id));
  const ownedCompanionIds = (stored?.ownedCompanionIds || []).filter(id =>
    getCompanionForGuild(id),
  );
  const equipped = stored?.equippedCompanionId;
  const equippedCompanionId = ownedCompanionIds.includes(equipped)
    ? equipped
    : COMPANION_NONE_ID;
  const ownedBadgeIds = (stored?.ownedBadgeIds || []).filter(id => getBadge(id));

  const slots = initialStoreSlots(ownedRelicIds, STORE_SLOT_COUNT);

  dispatch(
    setRelicVaultState({
      ownedRelicIds,
      ownedCompanionIds,
      ownedBadgeIds,
      equippedCompanionId,
      storeRelicIds: slots.ids,
      storeDeck: slots.deck,
      storeDeckPos: slots.pos,
    }),
  );
};

/**
 * Buy a relic from the store slots.
 * @returns {Promise<{success: boolean, error?: string, serverBalanceChanged?: boolean}>}
 */
export const buyRelic =
  ({ userId, relicId, balance }) =>
  async (dispatch, getState) => {
    const relic = getRelic(relicId);
    if (!relic) return failure(null, 'That relic no longer exists.');
    if (getState().relicVault.ownedRelicIds.includes(relicId)) {
      return failure(null, 'Already in your vault.');
    }
    if ((balance ?? 0) < relic.price) {
      return failure(null, `You need ${relic.price - (balance ?? 0)} more QuikCoins.`);
    }

    try {
      const resp = await relicVaultService.purchaseRelic({
        userId,
        relicId,
        price: relic.price,
      });
      if (!resp?.success) return failure(resp?.error, 'Purchase failed.');
      dispatch(addOwnedRelic(relicId));
      return { success: true, serverBalanceChanged: applyCoins(dispatch, resp, -relic.price) };
    } catch (err) {
      return failure(err?.message, 'Purchase failed.');
    }
  };

/**
 * Buy a guild's companion. A newly bought companion is equipped immediately.
 * @returns {Promise<{success: boolean, error?: string, serverBalanceChanged?: boolean}>}
 */
export const buyCompanion =
  ({ userId, guildId, balance }) =>
  async (dispatch, getState) => {
    const companion = getCompanionForGuild(guildId);
    if (!companion) return failure(null, 'That guardian no longer exists.');
    if (getState().relicVault.ownedCompanionIds.includes(guildId)) {
      return failure(null, 'Already a companion.');
    }
    if ((balance ?? 0) < companion.price) {
      return failure(
        null,
        `You need ${companion.price - (balance ?? 0)} more QuikCoins.`,
      );
    }

    try {
      const resp = await relicVaultService.purchaseCompanion({
        userId,
        guildId,
        price: companion.price,
      });
      if (!resp?.success) return failure(resp?.error, 'Purchase failed.');
      dispatch(addOwnedCompanion(guildId));
      const serverBalanceChanged = applyCoins(dispatch, resp, -companion.price);
      await dispatch(equipCompanion({ userId, guildId }));
      return { success: true, serverBalanceChanged };
    } catch (err) {
      return failure(err?.message, 'Purchase failed.');
    }
  };

/**
 * Equip an owned companion, or "none" to send the guardian away. Applied
 * optimistically - Meeting sends `set_guardian` to Unity when this changes -
 * and a failed save only logs.
 */
export const equipCompanion =
  ({ userId, guildId }) =>
  async (dispatch, getState) => {
    const target = guildId || COMPANION_NONE_ID;
    if (
      target !== COMPANION_NONE_ID &&
      !getState().relicVault.ownedCompanionIds.includes(target)
    ) {
      return failure(null, 'Unlock this guardian first.');
    }

    dispatch(setEquippedCompanion(target));
    try {
      await relicVaultService.saveEquippedCompanion({ userId, guildId: target });
    } catch (err) {
      console.warn('[RelicVault] equip save failed', err?.message || err);
    }
    return { success: true };
  };

/**
 * Redraw the store slots for the next refresh cost of the day.
 * @returns {Promise<{success: boolean, error?: string, serverBalanceChanged?: boolean}>}
 */
export const refreshStoreSlots =
  ({ userId, balance }) =>
  async (dispatch, getState) => {
    const vault = getState().relicVault;
    const allowance = refreshAllowanceFor(vault);
    if (allowance.left <= 0) return failure(null, 'No refreshes left today.');
    if ((balance ?? 0) < allowance.nextCost) {
      return failure(null, `You need ${allowance.nextCost - (balance ?? 0)} more QuikCoins.`);
    }

    try {
      const resp = await relicVaultService.refreshStore({
        userId,
        cost: allowance.nextCost,
      });
      if (!resp?.success) return failure(resp?.error, 'Refresh failed.');

      const drawn = drawIds(
        vault.storeDeck,
        vault.storeDeckPos,
        STORE_SLOT_COUNT,
        vault.ownedRelicIds,
        vault.storeRelicIds,
      );
      dispatch(
        setStoreSlots({
          storeRelicIds: drawn.ids,
          storeDeck: drawn.deck,
          storeDeckPos: drawn.pos,
          lastRefreshDate: todayStr(),
          refreshesUsedToday: allowance.usedToday + 1,
        }),
      );
      return {
        success: true,
        serverBalanceChanged: applyCoins(dispatch, resp, -allowance.nextCost),
      };
    } catch (err) {
      return failure(err?.message, 'Refresh failed.');
    }
  };

/**
 * Sell an owned relic back for half its price.
 * @returns {Promise<{success: boolean, refund?: number, error?: string, serverBalanceChanged?: boolean}>}
 */
export const sellRelic =
  ({ userId, relicId }) =>
  async (dispatch, getState) => {
    const relic = getRelic(relicId);
    if (!relic) return failure(null, 'That relic no longer exists.');
    if (!getState().relicVault.ownedRelicIds.includes(relicId)) {
      return failure(null, 'Not in your vault.');
    }

    const refund = sellRefundFor(relic);
    try {
      const resp = await relicVaultService.sellRelic({ userId, relicId, refund });
      if (!resp?.success) return failure(resp?.error, 'Sale failed.');
      dispatch(removeOwnedRelic(relicId));
      return {
        success: true,
        refund,
        serverBalanceChanged: applyCoins(dispatch, resp, refund),
      };
    } catch (err) {
      return failure(err?.message, 'Sale failed.');
    }
  };
